import {
    ClickElement,
    ClickText,
    ClickType,
    GA4EventName,
    GA4FeatureCategory,
    type Product as TrackingProduct,
    trackAnalyticsEvent,
    trackEecAddToCart,
    trackEecProductDetail,
} from '@jsmdg/tracking';
import { isValueVoucher } from '../../../shared/product-safeguards';
import { type Location, type Product } from '../../../shared/types';
import { getPurchaseVariantForTracking } from '../../helper/getPurchaseType';
import { type PurchaseType } from '../../types/enums/purchaseType';

function trackAddToCartClicked(
    clickType: ClickType = ClickType.DefaultUpper,
    clickText: ClickText | string = ClickText.AddToCart,
): void {
    setTimeout(
        () =>
            trackAnalyticsEvent({
                category: 'ProductDetail',
                action: 'ButtonClick',
                label: 'AddToCart',
                eventData: {
                    eventName: GA4EventName.ClickEcomCta,
                    feature_category: GA4FeatureCategory.ProductdetailBuybox,
                    click_element: ClickElement.AddToCart,
                    click_text: clickText,
                    click_type: clickType,
                },
            }),
        0,
    );
}

function trackRatingClicked(): void {
    setTimeout(
        () =>
            trackAnalyticsEvent({
                category: 'ProductDetail',
                action: 'TextClick',
                label: 'Ratings',
                eventData: {
                    eventName: GA4EventName.ClickRating,
                    feature_category: GA4FeatureCategory.ProductdetailBuybox,
                },
            }),
        0,
    );
}

function trackEECAddToCartAdded(
    product: Product,
    locations: Location[],
    quantity: number,
    hasFriendsDiscount: boolean,
    purchaseType: PurchaseType | undefined,
): void {
    trackEecAddToCart({
        product: {
            id: product.productId,
            url: product.url,
            trackingName: product.trackingName,
            rating: product.rating,
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price: product.price,
            originalPrice: product.originalPrice,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            locationCount: locations.length,
            ...(!isValueVoucher(product)
                ? {
                      participants: {
                          min: product.minParticipants,
                          max: product.maxParticipants,
                      },
                  }
                : {}),
            hasFriendsDiscount,
            nudge: product.productNudge
                ? {
                      id: product.productNudge?.id,
                      text: product.productNudge?.text,
                  }
                : undefined,
        },
        quantity,
        variant: getPurchaseVariantForTracking(product.type, purchaseType),
    });
}

function trackEECProductDetailViewed(
    product: Product,
    locations: Location[],
    purchaseType: PurchaseType | undefined,
    minParticipants: number | undefined,
    maxParticipants: number | undefined,
): void {
    trackEecProductDetail(
        {
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price: product.price,
            originalPrice: product.originalPrice,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            rating: product.rating,
            locationCount: locations.length,
            participants: {
                min: minParticipants,
                max: maxParticipants,
            },
            nudge: product.productNudge
                ? {
                      id: product.productNudge?.id,
                      text: product.productNudge?.text,
                  }
                : undefined,
        } as TrackingProduct,
        getPurchaseVariantForTracking(product.type, purchaseType),
    );
}

export {
    trackAddToCartClicked,
    trackEECAddToCartAdded,
    trackEECProductDetailViewed,
    trackRatingClicked,
};
